import { useCallback } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { useMagicPenStore } from '@src/stores';

import { constrainCircleDrawing, constrainRectangleDrawing, generateUniqueId } from '../../helpers';

// Constants imports
import { DEFAULT_STROKE_WIDTH } from '../../magicPen.constants';

import { EMagicPenShape, ICanvasObject, ICreatedBy } from '@src/types';

const useShapeTool = (createdBy: ICreatedBy, handleAddObjects: (objects: ICanvasObject[]) => Promise<void>) => {
  const { shapeStartPoint, currentShape, setCurrentShape, setShapeStartPoint, brushColor, activeShape } =
    useMagicPenStore(
      useShallow((state) => ({
        shapeStartPoint: state.shapeStartPoint,
        currentShape: state.currentShape,
        setCurrentShape: state.setCurrentShape,
        setShapeStartPoint: state.setShapeStartPoint,
        brushColor: state.brushColor,
        activeShape: state.activeShape,
      }))
    );

  const handleShapeMouseMove = useCallback(
    (pos: { x: number; y: number }, stage: any) => {
      if (shapeStartPoint && currentShape) {
        if (currentShape.type === EMagicPenShape.CIRCLE) {
          // For circle, calculate radius from start point (which is the center)
          const deltaX = pos.x - shapeStartPoint.x;
          const deltaY = pos.y - shapeStartPoint.y;
          const radius = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

          // Constrain circle drawing to stay within canvas bounds
          const constrainedRadius = constrainCircleDrawing(
            { x: shapeStartPoint.x, y: shapeStartPoint.y },
            radius,
            stage
          );

          setCurrentShape({
            ...currentShape,
            width: constrainedRadius * 2,
            height: constrainedRadius * 2,
          });
        } else {
          // For rectangles and other shapes (arrow, line), constrain drawing to canvas bounds
          const constrainedDimensions = constrainRectangleDrawing(
            shapeStartPoint,
            pos,
            stage
          );
          setCurrentShape({
            ...currentShape,
            width: constrainedDimensions.width,
            height: constrainedDimensions.height,
          });
        }
      }
    },
    [shapeStartPoint, currentShape, setCurrentShape]
  );

  const handleShapeMouseUp = useCallback(async () => {
    if (currentShape) {
      if (Math.abs(currentShape.width || 0) > 5 && Math.abs(currentShape.height || 0) > 5) {
        await handleAddObjects([currentShape]);
      }
      setCurrentShape(null);
      setShapeStartPoint(null);
    }
  }, [currentShape, handleAddObjects, setCurrentShape, setShapeStartPoint]);

  const startShapeDrawing = useCallback(
    (pos: { x: number; y: number }) => {
      const id = generateUniqueId();
      const newShape: ICanvasObject = {
        id,
        type: activeShape!,
        points: [],
        stroke: brushColor,
        strokeWidth: DEFAULT_STROKE_WIDTH,
        createdBy,
        x: pos.x,
        y: pos.y,
        width: 0,
        height: 0,
      };
      setCurrentShape(newShape);
    },
    [activeShape, brushColor, createdBy, setCurrentShape]
  );

  return {
    handleShapeMouseMove,
    handleShapeMouseUp,
    startShapeDrawing,
  };
};

export default useShapeTool;
